import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsAPI } from '../services/api';
import type { DocumentCreate, DocumentUpdate } from '../types';
import { useErrorHandler } from './useErrorHandler';

interface UseDocumentsParams {
  owner?: string;
  task_id?: string;
  pinned_only?: boolean;
  folder?: string;
  limit?: number;
}

export const useDocuments = (params?: UseDocumentsParams) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const { data: documents = [], isLoading, error } = useQuery({
    queryKey: ['documents', params],
    queryFn: () => documentsAPI.getAll(params),
    retry: 1,
    onError: handleError,
  });

  const createMutation = useMutation({
    mutationFn: (data: DocumentCreate) => documentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, update }: { id: string; update: DocumentUpdate }) =>
      documentsAPI.update(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: handleError,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => documentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: handleError,
  });

  return {
    documents,
    isLoading,
    error: error as Error | null,
    createDocument: createMutation.mutateAsync,
    updateDocument: updateMutation.mutateAsync,
    deleteDocument: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};

export const useDocument = (id: string | null) => {
  const { handleError } = useErrorHandler();

  const { data: document, isLoading, error } = useQuery({
    queryKey: ['documents', id],
    queryFn: () => documentsAPI.get(id!),
    enabled: !!id,
    retry: 1,
    onError: handleError,
  });

  return {
    document: document ?? null,
    isLoading,
    error: error as Error | null,
  };
};
